import { ApolloClient } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import { csrClient } from '../../../apollo/api/avantArteClient';
import {
  GeneratedDelegateCashInput,
  GeneratedDelegateCashOutput,
  InputMaybe,
  useBlockchainGetDelegationsByDelegateLazyQuery,
} from '../../../types/generated';
import { captureError } from '../../../utils/capture';
import { useWeb3Cache } from './useWeb3Cache';

interface UseDelegateCashOptions {
  // the chain id we check the delegations against
  chainId?: string;
  // an optional client to make queries with (for mocking)
  client?: ApolloClient<any>;
  // weather or not to make the query,
  // we should make it false only in components that eagerly need delegations
  shouldQuery?: boolean;
  // a contract address used to filter the responses
  filterContractAddress?: string;
}

/**
 * Manages state for delegate cash related features
 */
export const useDelegateCash = (options: UseDelegateCashOptions = {}) => {
  const {
    chainId,
    client,
    filterContractAddress,
    shouldQuery = false,
  } = options;
  const cache = useWeb3Cache();
  const [makeQuery, query] = useBlockchainGetDelegationsByDelegateLazyQuery({
    client: client || csrClient,
  });

  const setActiveDelegation = (delegation: GeneratedDelegateCashOutput) => {
    cache.delegateCashStore.setSelectedDelegateCashDelegation(delegation);
  };

  const removeActiveDelegation = () => {
    cache.delegateCashStore.setSelectedDelegateCashDelegation(undefined);
  };

  useEffect(() => {
    if (query.data) {
      let response = query.data
        .blockchainGetDelegationsByDelegate as GeneratedDelegateCashOutput[];

      if (filterContractAddress) {
        response = response.filter((delegation) => {
          if (delegation.type === 'CONTRACT' || delegation.type === 'TOKEN') {
            return (
              delegation.contract.toLowerCase() ===
              filterContractAddress.toLowerCase()
            );
          }
          return true;
        });
      }

      cache.delegateCashStore.setDelegateCashDelegations(response);
    }
  }, [query.data]);

  useEffect(() => {
    if (shouldQuery && cache.walletAddress && cache.chainId) {
      makeQuery({
        variables: {
          input: {
            chain: chainId || cache.chainId,
            walletAddress: cache.walletAddress,
          },
        },
      });
    }
  }, [shouldQuery, cache.walletAddress, cache.chainId]);

  useEffect(() => {
    if (query.error) {
      captureError(query.error, {
        tags: {
          chain: chainId || cache.chainId,
          walletAddress: cache.walletAddress,
        },
      });
    }
  }, [query.error]);

  const activeVaultAddress =
    cache.delegateCashStore.selectedDelegateCashDelegation?.vault;

  const getGraphqlInput = useCallback(():
    | InputMaybe<GeneratedDelegateCashInput>
    | undefined => {
    if (!activeVaultAddress) {
      return undefined;
    }
    return {
      vault: activeVaultAddress,
    };
  }, [activeVaultAddress]);

  return {
    error: query.error,
    loading: query.loading,
    delegations: cache.delegateCashStore.delegateCashDelegations || [],
    selectedDelegation: cache.delegateCashStore.selectedDelegateCashDelegation,
    activeVaultAddress,
    setActiveDelegation,
    removeActiveDelegation,
    getGraphqlInput,
  } as const;
};
